import { useEffect, useRef } from 'react'

const REFRESH_MS = 60 * 1000

/**
 * Calls `fn` immediately on mount, then every 60 seconds while the tab is visible.
 * When the tab becomes hidden, the interval pauses; when it becomes visible again,
 * it fires once and resumes the interval.
 */
export default function useDashboardPolling(fn: () => void) {
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    let intervalId: number | null = null

    const start = () => {
      if (intervalId !== null) return
      intervalId = window.setInterval(() => fnRef.current(), REFRESH_MS)
    }
    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
    }

    fnRef.current()

    if (document.visibilityState === 'visible') start()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fnRef.current()
        start()
      } else {
        stop()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])
}
